/* eslint-disable react-hooks/exhaustive-deps */
import { Button, Checkbox, Input, Label, Select, Tooltip } from "@src/common/components";
import { QuestionMarkCircleIcon } from "@heroicons/react/24/outline";
import React, { useEffect, useState } from "react";
import {
  EndCustomer,
  FullUpdateEndCustomerMutation,
  InvoicePdfTemplate,
  InvoicingFrequency,
  EmailTemplateGroupFragmentFragment,
  EndCustomerPriceVisibilityType,
  PricingList,
  ScanRequirement,
  StopCompletionGuard,
  useFullUpdateEndCustomerMutation,
  useGetPricingListsLazyQuery,
  useUpdateEndCustomerNoteMutation,
  useGetEmailTemplateGroupsLazyQuery,
  UserRoleType,
} from "@api/graphql/generated/generated-types";
import { OnChangeParams } from "baseui/select";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { DatePicker } from "baseui/datepicker";
import { datePickerDateToISODate } from "@src/common/utils/timezoneConverters";
import AddressInput from "@src/orders/create/components/AddressInput";
import { formatPhoneNumber } from "@src/common/lib/textUtils";
import { showErrorToast } from "@src/common/lib/NetworkErrorHandling";
import { ApolloError, FetchResult } from "@apollo/client";
import { useCourieStore } from "@src/common/lib/store";
import InternalNote from "@src/common/components/Widgets/InternalNote";
import { FALLBACK_INVOICING_FREQUENCY_OPTION, INVOCING_FREQUENCY_OPTIONS, PORTAL_PRICE_VISIBILITY_OPTIONS, SCAN_REQUIREMENT_TYPE_OPTIONS, ScanRequirementOption } from "@src/endcustomers/constants";
import { CustomerAccountComments } from "@src/endcustomers/components/CustomerAccountComments";
import { useAuth } from "@src/common/hooks/useAuth";
import { useDriverQuestionListSelect } from "@src/endcustomers/hooks/useDriverQuestionListSelect";
import { useCourierBrandSelect } from "@src/endcustomers/hooks/useCourierBrandSelect";

type Props = {
  endCustomer: EndCustomer | null | undefined;
  invoicePdfTemplates: InvoicePdfTemplate[];
  onSuccess?: (res: FetchResult<FullUpdateEndCustomerMutation>) => void;
  onCancel?: () => void;
  maxWidth?: number | string;
  showNote?: boolean;
};

const STOP_COMPLETION_GUARD_OPTIONS = [
  {
    id: "",
    label: "Default",
  },
  {
    id: StopCompletionGuard.AllowBeforeReadyTime,
    label: "Allow",
  },
  {
    id: StopCompletionGuard.BlockBeforeReadyTime,
    label: "Block",
  },
];

const PAYROLL_PRICING_LIST_TOOLTIP = (
  <div className="max-w-xs text-left font-normal">
    Optional. When set, driver pay on this customer&apos;s orders is computed from this list instead of the
    driver&apos;s own payroll list.
  </div>
);

const schema = yup.object().shape({
  contactEmail: yup.string().email("Invalid email address").required("Email is required"),
  contactPhoneNumber: yup.string(),
  companyName: yup.string().required("Company Name is required"),
  firstName: yup.string(),
  lastName: yup.string(),
  suite: yup.string(),
  googlePlaceId: yup.string(),
  streetAddress: yup.string(),
  billingStreetAddress: yup.string(),
  billingCity: yup.string(),
  billingState: yup.string(),
  billingCountry: yup.string(),
  billingZip: yup.string(),
  invoicingFrequency: yup.string(),
  taxCode: yup.string().nullable(),
  totalPricingMultiplier: yup.number().nullable(),
  invoicePdfTemplateId: yup.string().nullable(),
  billingEmailsCommaSeparated: yup.string().nullable(),
  note: yup.string(),
  scanRequirementType: yup.string(),
  stopCompletionGuard: yup.string().nullable(),
  portalPriceVisibilityOverride: yup.string().nullable(),
  invoiceCycleStartDate: yup.date().nullable(),
  preAuthorizeAmountMinimum: yup.number().nullable(),
  preAuthorizeAmountMultiplier: yup.number().nullable(),
  driverQuestionListId: yup.string().nullable(),
  excludeConvenienceFees: yup.boolean()
});

function EditCustomerForm({
  endCustomer,
  invoicePdfTemplates,
  onSuccess,
  onCancel,
  maxWidth = "100%",
  showNote = false,
}: Props) {
  // contexts
  const { courierId, isRoleType } = useAuth();
  const { showToast } = useCourieStore();
  const {
    handleSubmit,
    control,
    getValues,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
  });
  // states
  const [priceListSelectValue, setPriceListSelectValue] = useState<PricingList | undefined>();
  const [payrollPriceListSelectValue, setPayrollPriceListSelectValue] = useState<PricingList | undefined>();
  const [emailTemplateGroupSelectValue, setEmailTemplateGroupSelectValue] =
    useState<EmailTemplateGroupFragmentFragment | undefined>();
  const [invoicePdfTemplateSelectValue, setInvoicePdfTemplateSelectValue] = useState<InvoicePdfTemplate | undefined>();
  // gql
  const [getPricingList, { data: PricingListData }] = useGetPricingListsLazyQuery();
  const [getEmailTemplateGroups, { data: EmailTemplateGroupData }] = useGetEmailTemplateGroupsLazyQuery();
  const [fullUpdateEndCustomerMutation, { loading }] = useFullUpdateEndCustomerMutation();
  const [updateEndCustomerNoteMutation, { loading: updateEndCustomerNoteMutationLoading }] =
    useUpdateEndCustomerNoteMutation();
  const {
    questionListOptions,
    selectedQuestionList,
    handleQuestionListChange,
    getQuestionListValue,
    loading: questionListsLoading,
  } = useDriverQuestionListSelect(endCustomer?.driverQuestionList);
  const {
    brandOptions,
    selectedBrand,
    handleBrandChange,
    getBrandValue,
    hasBrands,
    loading: brandsLoading,
  } = useCourierBrandSelect(courierId, endCustomer?.courierBrand);
  // useeffects
  useEffect(() => {
    if (endCustomer) {
      setPriceListSelectValue(endCustomer.pricingList ? (endCustomer.pricingList as PricingList) : undefined);
      setPayrollPriceListSelectValue(endCustomer.payrollPricingList ?? undefined);
      setEmailTemplateGroupSelectValue(endCustomer.emailTemplateGroup ? endCustomer.emailTemplateGroup : undefined);
      setInvoicePdfTemplateSelectValue(
        endCustomer.invoicePdfTemplate ? (endCustomer.invoicePdfTemplate as InvoicePdfTemplate) : undefined
      );
    }
    if (endCustomer && control) {
      reset({
        companyName: endCustomer.companyName || "",
        contactPhoneNumber: endCustomer.contactPhoneNumber || "",
        contactEmail: endCustomer.contactEmail || "",
        billingEmailsCommaSeparated: endCustomer.billingEmails.join(",") || "",
        firstName: endCustomer.firstName || "",
        lastName: endCustomer.lastName || "",
        suite: endCustomer.suite || "",
        googlePlaceId: endCustomer?.location?.googlePlaceId || "",
        streetAddress:
          (endCustomer?.location?.streetAddress &&
            `${endCustomer?.location?.streetAddress}, ${endCustomer?.location?.city}`) ||
          "",
        billingStreetAddress: endCustomer.billingStreetAddress || "",
        billingCity: endCustomer.billingCity || "",
        billingState: endCustomer.billingState || "",
        billingCountry: endCustomer.billingCountry || "",
        billingZip: endCustomer.billingZip || "",
        invoicingFrequency: endCustomer.invoicingFrequency || InvoicingFrequency.Daily,
        taxCode: endCustomer.taxCode || "",
        totalPricingMultiplier: endCustomer.totalPricingMultiplier || 0,
        invoicePdfTemplateId: endCustomer.invoicePdfTemplate?.id || null,
        note: endCustomer.note || "",
        scanRequirementType: endCustomer.scanRequirementType || "",
        stopCompletionGuard: endCustomer.stopCompletionGuard || "",
        portalPriceVisibilityOverride: endCustomer.portalPriceVisibilityOverride || "",
        invoiceCycleStartDate: endCustomer.invoiceCycleStartDate ? new Date(endCustomer.invoiceCycleStartDate.replaceAll('-', '/')) : null,
        preAuthorizeAmountMinimum: endCustomer.preAuthorizeAmountMinimum,
        preAuthorizeAmountMultiplier: endCustomer.preAuthorizeAmountMultiplier,
        driverQuestionListId: endCustomer.driverQuestionList?.id || null,
        excludeConvenienceFees: endCustomer.excludeConvenienceFees ?? false,
      });
    }
  }, [endCustomer, setValue]);

  useEffect(() => {
    if (!courierId) return;
    getPricingList({ variables: { courierId } });
    getEmailTemplateGroups({ variables: { courierId } });
  }, [courierId]);

  const onSubmit = async (data: any) => {
    if (endCustomer && courierId) {
      const variables = {
        courierId: courierId,
        id: endCustomer.id,
        companyName: data.companyName,
        contactPhoneNumber: data.contactPhoneNumber || "",
        contactEmail: data.contactEmail,
        billingEmails: data.billingEmailsCommaSeparated
          ? data.billingEmailsCommaSeparated.split(",").map((email) => email.trim())
          : undefined,
        firstName: data.firstName,
        lastName: data.lastName,
        pricingListId: priceListSelectValue?.id,
        payrollPricingListId: payrollPriceListSelectValue?.id || null,
        suite: data.suite,
        googlePlaceId: data.googlePlaceId,
        invoicingFrequency: data.invoicingFrequency as InvoicingFrequency,
        taxCode: data.taxCode || null,
        totalPricingMultiplier: data.totalPricingMultiplier || null,
        invoicePdfTemplateId: invoicePdfTemplateSelectValue?.id || null,
        emailTemplateGroupId: emailTemplateGroupSelectValue?.id || null,
        note: showNote ? data.note || "" : endCustomer?.note || "",
        scanRequirementType: (data.scanRequirementType as ScanRequirement) || null,
        stopCompletionGuard: (data.stopCompletionGuard as StopCompletionGuard) || null,
        portalPriceVisibilityOverride: (data.portalPriceVisibilityOverride as EndCustomerPriceVisibilityType) || null,
        invoiceCycleStartDate: data.invoiceCycleStartDate ? datePickerDateToISODate(data.invoiceCycleStartDate) : null,
        preAuthorizeAmountMinimum: data.preAuthorizeAmountMinimum,
        preAuthorizeAmountMultiplier: data.preAuthorizeAmountMultiplier,
        driverQuestionListId: selectedQuestionList?.id,
        courierBrandId: selectedBrand?.id || null,
        billingStreetAddress: data.billingStreetAddress,
        billingCity: data.billingCity,
        billingState: data.billingState,
        billingCountry: data.billingCountry,
        billingZip: data.billingZip,
        excludeConvenienceFees: data.excludeConvenienceFees,
      };
      Object.keys(variables).forEach((key) => {
        if (key === "contactPhoneNumber") return;
        if (variables[key] === "") {
          variables[key] = null;
        }
      });
      fullUpdateEndCustomerMutation({
        variables,
      })
        .then((res) => {
          showToast({
            message: "Customer updated successfully",
            type: "success",
          });
          if (onSuccess) {
            onSuccess(res);
          }
        })
        .catch((err) => {
          showErrorToast(err as ApolloError, showToast);
        });
    }
  };

  const streetAddress = watch("streetAddress");
  const watchInvoicingFrequency = watch("invoicingFrequency");
  const watchScanRequirementType = watch("scanRequirementType");
  const watchStopCompletionGuard = watch("stopCompletionGuard");
  const watchPortalPriceVisibilityOverride = watch("portalPriceVisibilityOverride");

  function getPricingListValue(pricingList: PricingList | undefined) {
    if (!pricingList) return undefined;
    return [{ id: pricingList.id, label: pricingList.name }];
  }

  function getEmailTemplateGroupValue(emailTemplateGroup: EmailTemplateGroupFragmentFragment | undefined) {
    if (!emailTemplateGroup) return undefined;
    return [{ id: emailTemplateGroup.id, label: emailTemplateGroup.name }];
  }

  function getInvoicePdfTemplateValue(template: InvoicePdfTemplate | undefined) {
    if (!template) return undefined;
    return [{ id: template.id, label: template.name }];
  }

  const handleAddressChange = (address: google.maps.places.AutocompletePrediction | undefined) => {
    if (address) {
      setValue("googlePlaceId", address?.place_id);
      setValue("streetAddress", address?.description);
    } else {
      setValue("googlePlaceId", "");
      setValue("streetAddress", "");
    }
  };

  const atLeastOneValid = (value1: string | undefined, value2: string | undefined, value3: string | undefined) => {
    return !!(value1 || value2 || value3);
  };

  const validateAtLeastOne = () => {
    const companyName = getValues("companyName");
    const firstName = getValues("firstName");
    const lastName = getValues("lastName");
    return atLeastOneValid(companyName, firstName, lastName) || "At least one input should be valid";
  };

  const transformedPricingLists =
    PricingListData?.pricingLists
      ?.slice()
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((item) => ({
        id: item.id,
        label: item.name,
      })) || [];

  const transformedEmailTemplateGroups =
    EmailTemplateGroupData?.emailTemplateGroups?.map((item) => ({
      id: item.id,
      label: item.name,
    })) || [];

  const transformedInvoicePdfTemplates =
    invoicePdfTemplates?.map((item) => ({
      id: item.id,
      label: item.name,
    })) || [];

  const handlePriceListSelectChange = (params: OnChangeParams) => {
    if (!params.option || params.option.id === "") {
      setPriceListSelectValue(undefined);
      return;
    }
    const selectedPriceList = PricingListData?.pricingLists?.find((item) => item.id === params.option?.id);
    if (selectedPriceList) {
      setPriceListSelectValue(selectedPriceList);
    }
  };

  const handlePayrollPriceListSelectChange = (params: OnChangeParams) => {
    if (!params.option || params.option.id === "") {
      setPayrollPriceListSelectValue(undefined);
      return;
    }
    const selectedPriceList = PricingListData?.pricingLists?.find((item) => item.id === params.option?.id);
    if (selectedPriceList) {
      setPayrollPriceListSelectValue(selectedPriceList);
    }
  };

  const handleEmailTemplateGroupSelectChange = (params: OnChangeParams) => {
    if (!params.option || params.option.id === "") {
      setEmailTemplateGroupSelectValue(undefined);
      return;
    }
    const selectedEmailTemplateGroup = EmailTemplateGroupData?.emailTemplateGroups?.find(
      (item) => item.id === params.option?.id
    );
    if (selectedEmailTemplateGroup) {
      setEmailTemplateGroupSelectValue(selectedEmailTemplateGroup);
    }
  };

  const handleInvoicePdfTemplateSelectChange = (params: OnChangeParams) => {
    if (!params.option || params.option.id === "") {
      setInvoicePdfTemplateSelectValue(undefined);
      return;
    }
    const selectedTemplate = invoicePdfTemplates?.find((item) => item.id === params.option?.id);
    if (selectedTemplate) {
      setInvoicePdfTemplateSelectValue(selectedTemplate);
    }
  };

  const saveInternalNote = async (textToSave?: string, endCustomerId?: string) => {
    if (!endCustomerId) {
      return false;
    }
    return updateEndCustomerNoteMutation({
      variables: {
        courierId: courierId || "",
        endCustomerId,
        note: textToSave || "",
      },
    })
      .then(() => {
        showToast({
          message: "Note updated successfully",
          type: "success",
        });
        return true;
      })
      .catch((error) => {
        showErrorToast(error as ApolloError, showToast);
        return false;
      });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-2 space-y-2 w-full" style={{ maxWidth }}>
      {showNote && (
        <InternalNote
          key={0}
          textareaValue={getValues("note")}
          saveAction={async (textToSave?: string) => {
            return await saveInternalNote(textToSave, endCustomer?.id);
          }}
          loading={updateEndCustomerNoteMutationLoading}
        />
      )}

      <div>
        <Label htmlFor="companyName" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
          Company Name
        </Label>
        {errors.companyName && (
          <span className="text-red-500 dark:text-red-400 text-xs ml-2 truncate">
            *At least one Company name, First or Last name should be valid
          </span>
        )}
        <Controller
          control={control}
          name="companyName"
          rules={{ validate: validateAtLeastOne }}
          render={({ field }) => (
            <Input
              size="mini"
              value={field.value}
              onChange={(event) => field.onChange(event.currentTarget.value)}
              onBlur={field.onBlur}
              type="text"
              placeholder=""
              error={errors.companyName ? true : false}
            />
          )}
        />
      </div>

      <div>
        <Label htmlFor="contactEmail" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
          Contact Email
          {errors.contactEmail && (
            <span className="text-red-500 dark:text-red-400 text-xs ml-2">{errors.contactEmail?.message?.toString()}</span>
          )}
        </Label>
        <Controller
          control={control}
          name="contactEmail"
          render={({ field }) => (
            <Input
              size="mini"
              value={field.value}
              onChange={(event) => field.onChange(event.currentTarget.value)}
              onBlur={field.onBlur}
              type="text"
              placeholder=""
              error={errors.contactEmail ? true : false}
            />
          )}
        />
      </div>

      <div>
        <Label htmlFor="billingEmailsCommaSeparated" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
          Billing Emails (comma separated)
          {errors.billingEmailsCommaSeparated && (
            <span className="text-red-500 dark:text-red-400 text-xs ml-2">{errors.billingEmailsCommaSeparated?.message?.toString()}</span>
          )}
        </Label>
        <Controller
          control={control}
          name="billingEmailsCommaSeparated"
          render={({ field }) => (
            <Input
              size="mini"
              value={field.value || ""}
              onChange={(event) => field.onChange(event.currentTarget.value)}
              onBlur={field.onBlur}
              type="text"
              placeholder="email1@example.com, email2@example.com"
              error={errors.billingEmailsCommaSeparated ? true : false}
            />
          )}
        />
      </div>

      <div>
        <Label htmlFor="contactPhoneNumber" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
          Contact Phone Number
          {errors.contactPhoneNumber && (
            <span className="text-red-500 dark:text-red-400 text-xs ml-2">{errors.contactPhoneNumber?.message?.toString()}</span>
          )}
        </Label>
        <Controller
          control={control}
          name="contactPhoneNumber"
          rules={{ required: true }}
          render={({ field }) => (
            <Input
              size="mini"
              value={field.value && formatPhoneNumber(field.value)}
              onBlur={field.onBlur}
              placeholder=""
              onChange={(event) => {
                event.target.value = formatPhoneNumber(event.target.value);
                field.onChange(event);
              }}
              error={errors.contactPhoneNumber ? true : false}
            />
          )}
        />
      </div>
      <div>
        <Label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Company Address</Label>
        <AddressInput handleAddressChange={handleAddressChange} control={control} size="mini" value={streetAddress} />
      </div>
      <div>
        <Label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Suite</Label>
        <Controller
          control={control}
          name="suite"
          render={({ field }) => (
            <Input
              size="mini"
              value={field.value}
              onChange={(event) => field.onChange(event.currentTarget.value)}
              onBlur={field.onBlur}
              type="text"
              placeholder=""
            />
          )}
        />
      </div>
      <div>
        <Label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
          Billing Address
        </Label>
        <div className="text-gray-500 text-xs mb-2">
          Optional. When set, overrides the company address on invoice Bill To.
        </div>
        <div className="space-y-2">
          <Controller
            control={control}
            name="billingStreetAddress"
            render={({ field }) => (
              <Input
                size="mini"
                value={field.value}
                onChange={(event) => field.onChange(event.currentTarget.value)}
                onBlur={field.onBlur}
                type="text"
                placeholder="Street Address"
              />
            )}
          />
          <div className="grid grid-cols-2 gap-4">
            <Controller
              control={control}
              name="billingCity"
              render={({ field }) => (
                <Input
                  size="mini"
                  value={field.value}
                  onChange={(event) => field.onChange(event.currentTarget.value)}
                  onBlur={field.onBlur}
                  type="text"
                  placeholder="City"
                />
              )}
            />
            <Controller
              control={control}
              name="billingState"
              render={({ field }) => (
                <Input
                  size="mini"
                  value={field.value}
                  onChange={(event) => field.onChange(event.currentTarget.value)}
                  onBlur={field.onBlur}
                  type="text"
                  placeholder="State"
                />
              )}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Controller
              control={control}
              name="billingCountry"
              render={({ field }) => (
                <Input
                  size="mini"
                  value={field.value}
                  onChange={(event) => field.onChange(event.currentTarget.value)}
                  onBlur={field.onBlur}
                  type="text"
                  placeholder="Country"
                />
              )}
            />
            <Controller
              control={control}
              name="billingZip"
              render={({ field }) => (
                <Input
                  size="mini"
                  value={field.value}
                  onChange={(event) => field.onChange(event.currentTarget.value)}
                  onBlur={field.onBlur}
                  type="text"
                  placeholder="Zip"
                />
              )}
            />
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
            First Name
          </Label>
          <Controller
            control={control}
            name="firstName"
            rules={{ validate: validateAtLeastOne }}
            render={({ field }) => (
              <Input
                size="mini"
                value={field.value}
                onChange={(event) => field.onChange(event.currentTarget.value)}
                onBlur={field.onBlur}
                type="text"
                placeholder=""
                error={errors.firstName ? true : false}
              />
            )}
          />
        </div>
        <div>
          <Label htmlFor="lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
            Last Name
          </Label>
          <Controller
            name="lastName"
            rules={{ validate: validateAtLeastOne }}
            control={control}
            render={({ field }) => (
              <Input
                size="mini"
                value={field.value}
                onChange={(event) => field.onChange(event.currentTarget.value)}
                onBlur={field.onBlur}
                type="text"
                placeholder=""
                error={errors.lastName ? true : false}
              />
            )}
          />
        </div>
      </div>
      <div>
        <Label className="dark:text-gray-200">Price List</Label>
        <Select
          size="mini"
          options={[{ id: "", label: "Base Pricing List" }, ...transformedPricingLists]}
          value={
            priceListSelectValue?.id
              ? getPricingListValue(priceListSelectValue)
              : [{ id: "", label: "Base Pricing List" }]
          }
          searchable={false}
          clearable={false}
          placeholder="Select Price List"
          onChange={handlePriceListSelectChange}
        />
      </div>

      <div>
        <div className="flex items-center gap-2">
          <Label className="dark:text-gray-200">Payroll Pricing List</Label>
          <Tooltip content={PAYROLL_PRICING_LIST_TOOLTIP}>
            <QuestionMarkCircleIcon
              className="h-4 w-4 cursor-pointer text-gray-400"
              aria-label="Payroll pricing list info"
            />
          </Tooltip>
        </div>
        <Select
          size="mini"
          options={[{ id: "", label: "None" }, ...transformedPricingLists]}
          value={
            payrollPriceListSelectValue?.id
              ? getPricingListValue(payrollPriceListSelectValue)
              : [{ id: "", label: "None" }]
          }
          searchable={false}
          clearable={false}
          placeholder="Select Payroll Pricing List"
          onChange={handlePayrollPriceListSelectChange}
        />
      </div>

      <div className={hasBrands ? "grid grid-cols-2 gap-4" : ""}>
        <div>
          <Label className="dark:text-gray-200">Email Template</Label> {/* We call groups templates in the frontend */}
          <Select
            size="mini"
            options={[{ id: "", label: "" }, ...transformedEmailTemplateGroups]}
            value={
              emailTemplateGroupSelectValue?.id
                ? getEmailTemplateGroupValue(emailTemplateGroupSelectValue)
                : [{ id: "", label: "" }]
            }
            searchable={true}
            clearable={true}
            placeholder="Select Email Template"
            onChange={handleEmailTemplateGroupSelectChange}
          />
        </div>

        {hasBrands && (
          <div>
            <Label className="dark:text-gray-200">Brand</Label>
            <Select
              size="mini"
              options={[{ id: "", label: "" }, ...brandOptions]}
              isLoading={brandsLoading}
              value={
                selectedBrand?.id
                  ? getBrandValue(selectedBrand)
                  : [{ id: "", label: "" }]
              }
              searchable={true}
              clearable={true}
              placeholder="Select Brand"
              onChange={handleBrandChange}
            />
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="dark:text-gray-200">Invoice Template (PDF)</Label>
          <Select
            size="mini"
            options={[{ id: "", label: "" }, ...transformedInvoicePdfTemplates]}
            value={
              invoicePdfTemplateSelectValue?.id
                ? getInvoicePdfTemplateValue(invoicePdfTemplateSelectValue)
                : [{ id: "", label: "" }]
            }
            searchable={true}
            clearable={true}
            placeholder="Select Invoice Template"
            onChange={handleInvoicePdfTemplateSelectChange}
          />
        </div>
        <div>
          <Label className="dark:text-gray-200">QR/Barcode Scanning</Label>
          <Select
            deleteRemoves={false}
            size="mini"
            escapeClearsValue={false}
            clearable={false}
            options={SCAN_REQUIREMENT_TYPE_OPTIONS}
            value={
              [
                SCAN_REQUIREMENT_TYPE_OPTIONS.find((item) => (item.id as string) === watchScanRequirementType) ||
                  SCAN_REQUIREMENT_TYPE_OPTIONS[0],
              ] as any
            }
            searchable={false}
            placeholder="Scan Requirement"
            getOptionLabel={({ option }) => (
              <div className="flex flex-col py-1">
                <span className="text-sm">{option?.label as string}</span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {(option as ScanRequirementOption | undefined)?.description}
                </span>
              </div>
            )}
            getValueLabel={({ option }) => <span>{option?.label as string}</span>}
            onChange={(options) => {
              if (!options || options.value.length === 0) return;
              setValue("scanRequirementType", options.value[0]?.id as ScanRequirement);
            }}
          />
        </div>
      </div>

      <div>
        <Label className="dark:text-gray-200">Invoice Frequency</Label>
        <Select
          deleteRemoves={false}
          size="mini"
          escapeClearsValue={false}
          clearable={false}
          options={INVOCING_FREQUENCY_OPTIONS}
          value={
            [
              INVOCING_FREQUENCY_OPTIONS.find((item) => (item.id as string) === watchInvoicingFrequency) ||
              FALLBACK_INVOICING_FREQUENCY_OPTION,
            ] as any
          }
          searchable={false}
          placeholder="Invoice Frequency"
          onChange={(options) => {
            if (!options || options.value.length === 0) return;
            setValue("invoicingFrequency", options.value[0]?.id as InvoicingFrequency);
          }}
        />        
      </div>
      
      {/* Invoice Cycle Start Date - Show for Weekly and Biweekly */}
      {(watchInvoicingFrequency === InvoicingFrequency.Weekly ||
        watchInvoicingFrequency === InvoicingFrequency.Biweekly ||
        watchInvoicingFrequency === InvoicingFrequency.WeeklySplitByMonth) && (
        <div>
          <Label htmlFor="invoiceCycleStartDate" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
            Invoice Cycle Start Date Override (Optional)
          </Label>
          <div className="text-gray-500 text-xs mb-2">
            If provided, it will override the default.
          </div>
          {errors.invoiceCycleStartDate && (
            <span className="text-red-500 dark:text-red-400 text-xs ml-2">{errors.invoiceCycleStartDate?.message?.toString()}</span>
          )}
          <Controller
            control={control}
            name="invoiceCycleStartDate"
            render={({ field: { value, onChange } }) => (
              <DatePicker
                clearable={true}
                value={value}
                formatString="MM/dd/yyyy"
                onChange={({ date }) => onChange(date)}
                error={errors.invoiceCycleStartDate ? true : false}
              />
            )}
          />
        </div>
      )}
      
      {watchInvoicingFrequency == InvoicingFrequency.PerOrderPreAuthorize && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="preAuthorizeAmountMinimum" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
              Pre-Authorization Minimum
            </Label>
            <Controller
              control={control}
              name="preAuthorizeAmountMinimum"
              render={({ field }) => (
                <Input
                  size="mini"
                  error={errors.preAuthorizeAmountMinimum ? true : false}
                  value={field.value as string | number | undefined}
                  onChange={(event) => {
                    const value = event.currentTarget.value === "" ? null : parseFloat(event.currentTarget.value);
                    field.onChange(value);
                  }}
                  onBlur={field.onBlur}
                  type="number"
                  placeholder=""
                />
              )}
            />
          </div>
          <div>
            <Label htmlFor="preAuthorizeAmountMultiplier" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
              Pre-Authorization Multiplier
            </Label>
            <Controller
              control={control}
              name="preAuthorizeAmountMultiplier"
              render={({ field }) => (
                <Input
                  size="mini"
                  error={errors.preAuthorizeAmountMultiplier ? true : false}
                  value={field.value as string | number | undefined}
                  onChange={(event) => {
                    const value = event.currentTarget.value === "" ? null : parseFloat(event.currentTarget.value);
                    field.onChange(value);
                  }}
                  onBlur={field.onBlur}
                  type="number"
                  placeholder=""
                />
              )}
            />
          </div>
        </div>
      )}
      <div className="flex gap-2 items-center">
        <Controller
          control={control}
          name="excludeConvenienceFees"
          render={({ field }) => (
            <Checkbox
              id="excludeConvenienceFees"
              checked={field.value ?? false}
              onChange={(e) => field.onChange(e.target.checked)}
            />
          )}
        />
        <Label htmlFor="excludeConvenienceFees" className="dark:text-gray-200">
          Exempt from convenience fee
        </Label>
      </div>
      <div>
        <Label className="dark:text-gray-200">Driver Subtask List</Label>
        <Select
          size="mini"
          options={questionListOptions}
          isLoading={questionListsLoading}
          value={
            selectedQuestionList?.id
              ? getQuestionListValue(selectedQuestionList)
              : []
          }
          searchable={true}
          clearable={true}
          placeholder="Select Driver Subtask List"
          onChange={handleQuestionListChange}
        />
      </div>
      <div>
        <Label className="dark:text-gray-200">Allow drivers to complete task before ready time?</Label>
        <Select
          deleteRemoves={false}
          size="mini"
          escapeClearsValue={false}
          clearable={false}
          options={STOP_COMPLETION_GUARD_OPTIONS}
          value={
            [
              STOP_COMPLETION_GUARD_OPTIONS.find((item) => (item.id as string) === watchStopCompletionGuard) ||
                STOP_COMPLETION_GUARD_OPTIONS[0],
            ] as any
          }
          searchable={false}
          placeholder=""
          onChange={(options) => {
            if (!options || options.value.length === 0) return;
            setValue("stopCompletionGuard", options.value[0]?.id as StopCompletionGuard);
          }}
        />
      </div>
      <div>
        <Label className="dark:text-gray-200">Customer portal price visibility</Label>
        <Select
          deleteRemoves={false}
          size="mini"
          escapeClearsValue={false}
          clearable={false}
          options={PORTAL_PRICE_VISIBILITY_OPTIONS}
          value={
            [
              PORTAL_PRICE_VISIBILITY_OPTIONS.find((item) => item.id === (watchPortalPriceVisibilityOverride || "")) ||
                PORTAL_PRICE_VISIBILITY_OPTIONS[0],
            ] as any
          }
          searchable={false}
          placeholder=""
          onChange={(options) => {
            if (!options || options.value.length === 0) return;
            setValue("portalPriceVisibilityOverride", options.value[0]?.id as string);
          }}
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="taxCode" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
            Tax Code
          </Label>
          <Controller
            control={control}
            name="taxCode"
            render={({ field }) => (
              <Input
                size="mini"
                value={field.value as string | number | undefined}
                onChange={(event) => field.onChange(event.currentTarget.value)}
                onBlur={field.onBlur}
                type="text"
                placeholder=""
              />
            )}
          />
        </div>
        <div>
          <Label htmlFor="totalPricingMultiplier" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
            Pricing Multiplier
          </Label>
          <Controller
            control={control}
            name="totalPricingMultiplier"
            render={({ field }) => (
              <Input
                size="mini"
                value={field.value as string | number | undefined}
                onChange={(event) => {
                  const value = event.currentTarget.value === "" ? null : parseFloat(event.currentTarget.value);
                  field.onChange(value);
                }}
                onBlur={field.onBlur}
                type="number"
                placeholder=""
              />
            )}
          />
        </div>
      </div>
      <hr className="my-3" />      
      <div className="flex justify-end">
        {onCancel && (
          <Button
            className="mr-4 border"
            color={"secondary"}
            onClick={onCancel}
          >
            Cancel
          </Button>
        )}
        <Tooltip
          content={
            isRoleType(UserRoleType.Administrator)
              ? "Save Changes"
              : "Insufficient Permissions"
          }
        >
          <Button
            type="submit"
            isProcessing={loading}
            disabled={!isRoleType(UserRoleType.Administrator)}
          >
            Save
          </Button>
        </Tooltip>
      </div>
    </form>
  );
}

export default EditCustomerForm;